import React from 'react';
import _ from 'lodash';
import ItemSelector from 'components/items_selector/items_selector';
import Label from 'components/label';
import { Checkbox } from '@atlaskit/checkbox';

class Form extends React.Component {
  /**
   * This is a composition form that displays the ItemSelectors and subscriber checkbox for PageAccessUser creation
   * and update. It controls the logic around showing and hiding the different ItemSelectors and the subscribe
   * checkbox.
   *
   * @param {object} props
   * @param {string} props.group.items - A collection of pag items to display.  Minimum required field is `name`
   * @param {string} props.group.preselectedItemIds - A collection of preselected group item ids
   * @param {string} props.group.hiddenFieldName
   * @param {string} props.group.hiddenFieldId
   * @param {string} props.component.items - A collection of component items to display.  Minimum required field is `name`
   * @param {string} props.component.preselectedItemIds - A collection of preselected component item ids
   * @param {string} props.component.hiddenFieldName
   * @param {string} props.component.hiddenFieldId
   * @param {string} props.metric.items - A collection of metric items to display.  Minimum required field is `name`
   * @param {string} props.metric.preselectedItemIds - A collection of preselected metric item ids
   * @param {string} props.metric.hiddenFieldName
   * @param {string} props.metric.hiddenFieldId
   * @param {string} props.subscribe.include - will show the subscribe checkbox if true. We only show that checkbox
   *                                           when a new PageAccessUser is being created
   */
  constructor(props) {
    super(props);
    this.state = {
      subscribeShow: false,
      componentShow: this.props.group.preselectedItemIds.length == 0,
      metricShow: this.props.group.preselectedItemIds.length == 0,
    };
  }

  _handlePageAccessGroupOnToggle(items) {
    // We don't show the component or metrics selectors if a user has selected a group in the UI
    var pageAccessGroups = _.filter(items, item => !!item.selected);
    if (pageAccessGroups.length == 0) {
      // Show the components and metrics ItemSelectors
      this.setState({ componentShow: true });
      this.setState({ metricShow: true });
      // Don't show the subscribe checkbox, since no groups are selected the component selections will control the hide/show for that
      this.setState({ subscribeShow: false });
    } else {
      // Since at least one PAG is selected we hide the components and metrics ItemsSelectors
      this.setState({ componentShow: false });
      this.setState({ metricShow: false });

      // Check to see if we need to be showing the subscribe checkbox because of the PAG selections
      // We only show the subscribe checkbox if a selected PAG has associated components
      var pageAccessGroup = _.find(
        pageAccessGroups,
        pageAccessGroup => pageAccessGroup.component_ids.length > 0,
      );
      if (pageAccessGroup != null) {
        // Show the subscription checkbox
        this.setState({ subscribeShow: true });
      } else {
        // Hide the subscription checkbox
        this.setState({ subscribeShow: false });
      }
    }
  }

  _handleComponentOnToggle(items) {
    // We want to show the subscribe checkbox if the user has selected any of the components in the UI
    var comps = _.filter(items, item => !!item.selected);
    if (comps.length == 0) {
      this.setState({ subscribeShow: false });
    } else {
      this.setState({ subscribeShow: true });
    }
  }

  _pageAccessGroups() {
    if (this.props.group.items.length > 0) {
      return (
        <div className="associated-groups">
          <Label
            text="Associated groups"
            className="associated-components-label"
            tooltipText={
              'Add the relevant ' +
              this.props.group.display_name +
              's that this user should be associated with'
            }
          />
          <ItemSelector
            className="inline"
            items={this.props.group.items}
            preselectedItemIds={this.props.group.preselectedItemIds}
            hiddenFieldName={this.props.group.hiddenFieldName}
            onToggle={this._handlePageAccessGroupOnToggle.bind(this)}
            hiddenFieldId={this.props.group.hiddenFieldId}
            use_adg3={this.props.use_adg3}
          />
        </div>
      );
    }
  }

  _components() {
    if (this.props.component.items.length > 0 && this.state.componentShow) {
      return (
        <div>
          <Label
            text="Associated components"
            className="associated-components-label"
            tooltipText="Add the relevant components that this user should be associated with"
          />

          <ItemSelector
            className="inline"
            items={this.props.component.items}
            preselectedItemIds={this.props.component.preselectedItemIds}
            hiddenFieldName={this.props.component.hiddenFieldName}
            onToggle={this._handleComponentOnToggle.bind(this)}
            hiddenFieldId={this.props.component.hiddenFieldId}
            use_adg3={this.props.use_adg3}
          />
        </div>
      );
    }
  }

  _subscribe() {
    if (this.props.subscribe.include && this.state.subscribeShow) {
      return (
        <div
          className="control-group access-user-notification"
          data-js-hook="subscription-ui"
        >
          <label>Subscribe to notifications</label>
          <div className="controls">
            {this.props.use_adg3 ? (
              <Checkbox
                value="1"
                name="page_access_user[subscribe_to_components]"
                label={
                  <span id="page_access_user_subscribe_to_components">
                    Also subscribe this user to the components they have access
                    to
                  </span>
                }
              />
            ) : (
              <label className="checkbox">
                <input
                  id="page_access_user_subscribe_to_components"
                  name="page_access_user[subscribe_to_components]"
                  type="checkbox"
                  value="1"
                />
                Also subscribe this user to the components they have access to
              </label>
            )}
            <div className="help-block">
              User will <strong>not</strong> receive a confirmation email for
              the subscription.
            </div>
          </div>
          <br />
        </div>
      );
    }
  }

  _metrics() {
    if (this.props.metric.items.length > 0 && this.state.metricShow) {
      return (
        <div className="associated-metrics">
          <Label
            text="Associated metrics"
            className="associated-metrics-label"
            tooltipText="Add the relevant metrics that this user should be associated with"
          />

          <ItemSelector
            className="inline"
            items={this.props.metric.items}
            preselectedItemIds={this.props.metric.preselectedItemIds}
            hiddenFieldName={this.props.metric.hiddenFieldName}
            hiddenFieldId={this.props.metric.hiddenFieldId}
            use_adg3={this.props.use_adg3}
          />
        </div>
      );
    }
  }

  render() {
    return (
      <div>
        {this._pageAccessGroups()}
        {this._components()}
        {this._metrics()}
        {this._subscribe()}
      </div>
    );
  }
}

export default Form;
